/**
 * Cookie / tracker consent — single source of truth.
 *
 * Categories (extend as needed):
 *  - essential  → always on, cannot be disabled (auth session, CSRF, UI prefs).
 *  - analytics  → audience measurement (GA, Plausible, PostHog autocapture).
 *  - marketing  → ads / retargeting (Meta Pixel, Google Ads, TikTok).
 *
 * Stored in localStorage as JSON so we can extend without breaking old values.
 * Emits a `window` event when changed so listeners (ConsentGate, scripts) react.
 *
 * GDPR / Legea 506/2004:
 *  - Banner must appear BEFORE any non-essential script loads.
 *  - User must be able to refuse as easily as accept (we expose "Doar esențiale").
 *  - User must be able to change their mind anytime (footer "Cookie-uri" link).
 *  - Consent expires after 12 months (we re-prompt).
 */

export type ConsentCategory = "essential" | "analytics" | "marketing";

export type ConsentState = {
  essential: true; // always true
  analytics: boolean;
  marketing: boolean;
  /** ISO timestamp when the user last made a choice. */
  decidedAt: string | null;
};

const STORAGE_KEY = "ez_cookie_consent_v2";
const LEGACY_KEY = "ez_cookie_consent"; // v1 (string "all" | "essential")
const EVENT_NAME = "ez:consent-change";
const OPEN_EVENT_NAME = "ez:consent-open";
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

const DEFAULT: ConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
  decidedAt: null,
};

function migrateLegacy(): ConsentState | null {
  if (typeof window === "undefined") return null;
  const legacy = localStorage.getItem(LEGACY_KEY);
  if (!legacy) return null;
  const accepted = legacy === "all";
  const migrated: ConsentState = {
    essential: true,
    analytics: accepted,
    marketing: accepted,
    decidedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
  localStorage.removeItem(LEGACY_KEY);
  return migrated;
}

export function readConsent(): ConsentState {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const migrated = migrateLegacy();
      return migrated ?? DEFAULT;
    }
    const parsed = JSON.parse(raw) as ConsentState;
    // Re-prompt if older than MAX_AGE_MS
    if (parsed.decidedAt) {
      const age = Date.now() - new Date(parsed.decidedAt).getTime();
      if (age > MAX_AGE_MS) return DEFAULT;
    }
    return { ...DEFAULT, ...parsed, essential: true };
  } catch {
    return DEFAULT;
  }
}

export function writeConsent(next: Partial<ConsentState>) {
  if (typeof window === "undefined") return;
  const merged: ConsentState = {
    ...readConsent(),
    ...next,
    essential: true,
    decidedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: merged }));
}

export function hasDecided(state: ConsentState = readConsent()): boolean {
  return state.decidedAt !== null;
}

export function reopenBanner() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT_NAME, { detail: readConsent() }));
}

export const CONSENT_EVENT = EVENT_NAME;
export const CONSENT_OPEN_EVENT = OPEN_EVENT_NAME;
